import { api } from './api';

export const getServiceDetails = async (serviceId) => {
  try {
    const response = await api.get(`/api/services/${serviceId}`);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get nearby vendors within a specified radius
 * @param {number} latitude - Latitude coordinate
 * @param {number} longitude - Longitude coordinate  
 * @param {number} radiusKm - Search radius in kilometers (default: 50)
 * @param {number} limit - Maximum number of vendors to return (default: 10)
 * @returns {Promise<Object>} Vendor locations data
 */
export const getNearbyVendors = async (latitude, longitude, radiusKm = 50, limit = 10, serviceSubtypeId = null) => {
  try {
    // Ensure parameters are numbers
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);
    const radius = parseFloat(radiusKm);
    const limitValue = parseInt(limit);

    // Manually construct URL with query parameters
    const queryParams = new URLSearchParams({
      latitude: lat.toString(),
      longitude: lng.toString(),
      radius_km: radius.toString(),
      limit: limitValue.toString()
    });

    // Add service subtype filter if provided
    if (serviceSubtypeId) {
      queryParams.append('service_subtype_id', serviceSubtypeId.toString());
    }

    const url = `/api/agent/nearby-vendors?${queryParams.toString()}`;

    const response = await api.get(url);
    return response;
  } catch (error) {
    throw error;
  }
};


export const updateService = async (serviceId, updateData) => {
  try {
    const response = await api.put(`/api/services/${serviceId}`, updateData);
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const getCostEstimate = async (
  vendorLocationId,
  distanceKm,
  schemeServiceTypeId,
  schemeServiceSubtypeId = null,
  isPaidCustomer = false,
  options = {},
) => {
  try {
    const requestData = {
      vendor_location_id: vendorLocationId,
      distance_km: distanceKm,
      scheme_service_type_id: schemeServiceTypeId,
      scheme_service_subtype_id: schemeServiceSubtypeId,
      is_paid_customer: isPaidCustomer,
    };

    if (options.clientSchemeId) {
      requestData.client_scheme_id = options.clientSchemeId;
    }
    if (options.clientServiceRatesId) {
      requestData.client_service_rates_id = options.clientServiceRatesId;
    }

    const response = await api.post('/api/services/cost-estimate', requestData);
    return response;
  } catch (error) {
    console.error('Cost estimate API error:', error);
    console.error('Error response:', error.response);
    throw error;
  }
};

export const getClientServiceRates = async (clientSchemeId, serviceTypeCode = null, serviceSubtypeCode = null) => {
  try {
    const queryParams = new URLSearchParams({
      client_scheme_id: String(clientSchemeId),
    });
    if (serviceTypeCode) queryParams.append('service_type_code', serviceTypeCode);
    if (serviceSubtypeCode) queryParams.append('service_subtype_code', serviceSubtypeCode);
    const response = await api.get(`/api/services/client-service-rates?${queryParams.toString()}`);
    return response;
  } catch (error) {
    console.error('Client service rates API error:', error);
    throw error;
  }
};

export const getVendorLocationDrivers = async (vendorLocationId) => {
  try {
    const response = await api.get(`/api/vendors/list-drivers?vendor_location_id=${vendorLocationId}`);
    return response;
  } catch (error) {
    throw error;
  }
};

// Service subtypes API call (same as AddService)
export const getServiceSubtypes = async (serviceTypeId) => {
  try {
    const url = `/api/cases/service-types/${serviceTypeId}/subtypes/?is_active=true`;
    const response = await api.get(url);
    return response;
  } catch (error) {
    throw error;
  }
};

/**
 * Get vendor service rates for a specific vendor
 * @param {number} vendorId - Vendor ID
 * @returns {Promise<Object>} Vendor service rates data
 */
export const getVendorServiceRates = async (vendorId) => {
  try {
    const url = `/api/vendors/${vendorId}/service-rates`;
    const response = await api.get(url);
    return response; // Return response directly, not response.data
  } catch (error) {
    console.error('Vendor service rates API error:', error);
    console.error('Error response:', error.response);
    throw error;
  }
};
